import Anser from "anser";
import type { ElementContent, Node, Root, RootContent } from "hast";
import { defaultSchema } from "hast-util-sanitize";
import { buildAnsiClassName, escapeCarriageReturn } from "../TextWithLinks/AnsiColors";

// Convert literal escape sequence representations (\x1b, \033, \e) to actual
// ESC bytes (0x1B). Server-side variable substitution and JSON serialization
// often convert the raw ESC byte to its text representation, but Anser only
// recognizes actual ESC bytes.
function unescapeAnsiLiterals(text: string): string {
    return text
        .replace(/\\x1b/g, "\x1b")
        .replace(/\\033/g, "\x1b")
        .replace(/\\e(\[)/g, "\x1b$1");
}

type HastNode = Root | RootContent;

// eslint-disable-next-line no-control-regex
const ANSI_REGEX = /(?:\x1b|\u001b)\[/;

function containsAnsi(text: string): boolean {
    return ANSI_REGEX.test(text);
}

function ansiTextToHastNodes(text: string): ElementContent[] {
    const escaped = escapeCarriageReturn(text);
    const entries = Anser.ansiToJson(escaped, { use_classes: true, remove_empty: true });

    return entries.map((entry): ElementContent => {
        const className = buildAnsiClassName(entry);
        if (className) {
            return {
                type: "element",
                tagName: "span",
                properties: { className },
                children: [{ type: "text", value: entry.content }],
            };
        }
        return { type: "text", value: entry.content };
    });
}

function walkTree(node: HastNode): HastNode | ElementContent[] {
    if (node.type === "text") {
        const unescaped = unescapeAnsiLiterals(node.value);
        if (!containsAnsi(unescaped)) {
            return node;
        }
        const hastNodes = ansiTextToHastNodes(unescaped);
        if (hastNodes.length === 1) {
            return hastNodes[0];
        }
        return hastNodes;
    }

    if (!("children" in node)) {
        return node;
    }

    // After narrowing: node is Root | Element (both have children).
    // ElementContent[] is assignable to both Root.children (RootContent[])
    // and Element.children (ElementContent[]).
    const newChildren: ElementContent[] = [];
    for (const child of node.children) {
        const result = walkTree(child);
        if (Array.isArray(result)) {
            newChildren.push(...result);
        } else if (result.type !== "root" && result.type !== "doctype") {
            newChildren.push(result);
        }
    }

    return { ...node, children: newChildren };
}

function isHastNode(node: Node): node is HastNode {
    switch (node.type) {
        case "root":
        case "element":
        case "text":
        case "comment":
        case "doctype":
            return true;
        default:
            return false;
    }
}

export function rehypeAnsiColors() {
    return (tree: Node) => {
        if (!isHastNode(tree)) return;
        const result = walkTree(tree);
        if (!Array.isArray(result)) return result;
    };
}

// Extended sanitize schema based on hast-util-sanitize's default GitHub schema.
// We add "className" to the allowed attributes for span elements so that
// Anser's class-based ANSI color spans (e.g. <span class="ansi-red-fg">)
// survive sanitization. hast-util-sanitize uses xtend (shallow merge), so we
// must provide the complete schema — spreading the default and overriding the
// nested `attributes` key lets us do this without duplicating the entire schema.
export const ansiSanitizeSchema = {
    ...defaultSchema,
    attributes: {
        ...defaultSchema.attributes,
        span: ["className"],
    },
};
